<template>
  <span class="field-help-label">
    <v-menu
      v-if="text"
      v-model="menuOpen"
      offset-y
      :nudge-bottom="6"
      :open-on-hover="false"
      :open-on-click="false"
      :close-on-content-click="true"
      :close-on-click="true"
      max-width="360"
      content-class="field-help-label__menu"
      @input="onMenuInput"
    >
      <template v-slot:activator="{ attrs }">
        <span
          class="field-help-label__label field-help-label__label--help"
          v-bind="attrs"
          @mouseenter="onActivatorEnter"
          @mouseleave="onActivatorLeave"
          @click.prevent.stop="onActivatorClick"
        >{{ label }}</span>
      </template>
      <v-sheet
        class="pa-3 field-help-label__sheet"
        elevation="2"
        @mouseenter="onPopoverEnter"
        @mouseleave="onPopoverLeave"
      >
        <span class="field-help-label__text">{{ text }}</span>
      </v-sheet>
    </v-menu>
    <span v-else class="field-help-label__label">{{ label }}</span>

    <v-menu
      v-if="isFunctional"
      v-model="flagMenuOpen"
      offset-y
      :nudge-bottom="6"
      :open-on-hover="false"
      :open-on-click="false"
      :close-on-content-click="true"
      :close-on-click="true"
      max-width="360"
      content-class="field-help-label__menu"
      @input="onFlagMenuInput"
    >
      <template v-slot:activator="{ attrs }">
        <span
          class="field-help-label__flag"
          v-bind="attrs"
          aria-label="Functional field"
          @mouseenter="onFlagEnter"
          @mouseleave="onFlagLeave"
        >🚩</span>
      </template>
      <v-sheet
        class="pa-3 field-help-label__sheet"
        elevation="2"
        @mouseenter="onFlagPopoverEnter"
        @mouseleave="onFlagLeave"
      >
        <span class="field-help-label__text">
          Functional field — present on Arena feed
        </span>
      </v-sheet>
    </v-menu>
  </span>
</template>

<script>
import {
  GAME_FIELD_DESCRIPTIONS,
  GAME_FIELD_FUNCTIONAL,
} from '@/constants/gameFieldDescriptions';

export default {
  name: 'FieldHelpLabel',
  props: {
    label: {
      type: String,
      required: true,
    },
    field: {
      type: String,
      default: '',
    },
    help: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      menuOpen: false,
      flagMenuOpen: false,
      /** 'hover' | 'tap' */
      openMode: null,
      hoverCloseTimer: null,
      flagCloseTimer: null,
      /** Ignore the synthetic click that follows touch when we already opened via tap. */
      ignoreNextClick: false,
    };
  },
  computed: {
    text() {
      if (this.help) return this.help;
      if (!this.field) return '';
      return GAME_FIELD_DESCRIPTIONS[this.field] || '';
    },
    isFunctional() {
      return Boolean(this.field && GAME_FIELD_FUNCTIONAL.has(this.field));
    },
  },
  beforeDestroy() {
    this.clearHoverCloseTimer();
    this.clearFlagCloseTimer();
  },
  methods: {
    onFlagEnter() {
      this.clearFlagCloseTimer();
      this.flagMenuOpen = true;
    },
    onFlagLeave() {
      this.clearFlagCloseTimer();
      this.flagCloseTimer = setTimeout(() => {
        this.flagMenuOpen = false;
      }, 140);
    },
    onFlagPopoverEnter() {
      this.clearFlagCloseTimer();
    },
    clearFlagCloseTimer() {
      if (this.flagCloseTimer) {
        clearTimeout(this.flagCloseTimer);
        this.flagCloseTimer = null;
      }
    },
    onFlagMenuInput(open) {
      if (!open) {
        this.clearFlagCloseTimer();
      }
    },
    onActivatorEnter() {
      this.clearHoverCloseTimer();
      this.openMode = 'hover';
      this.menuOpen = true;
    },
    onActivatorLeave() {
      if (this.openMode === 'hover') {
        this.scheduleHoverClose();
      }
    },
    onActivatorClick(e) {
      if (e && e.pointerType === 'touch') {
        this.ignoreNextClick = true;
      }
      if (this.ignoreNextClick && e && e.pointerType !== 'touch') {
        this.ignoreNextClick = false;
        return;
      }

      this.clearHoverCloseTimer();
      if (this.menuOpen && this.openMode === 'tap') {
        this.menuOpen = false;
        this.openMode = null;
        return;
      }
      this.openMode = 'tap';
      this.menuOpen = true;
    },
    onPopoverEnter() {
      if (this.openMode === 'hover') {
        this.clearHoverCloseTimer();
      }
    },
    onPopoverLeave() {
      if (this.openMode === 'hover') {
        this.scheduleHoverClose();
      }
    },
    scheduleHoverClose() {
      this.clearHoverCloseTimer();
      this.hoverCloseTimer = setTimeout(() => {
        if (this.openMode === 'hover') {
          this.menuOpen = false;
          this.openMode = null;
        }
      }, 140);
    },
    clearHoverCloseTimer() {
      if (this.hoverCloseTimer) {
        clearTimeout(this.hoverCloseTimer);
        this.hoverCloseTimer = null;
      }
    },
    onMenuInput(open) {
      if (!open) {
        this.openMode = null;
        this.clearHoverCloseTimer();
      }
    },
  },
};
</script>

<style scoped>
.field-help-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
}

/* Vuetify labels use pointer-events: none; re-enable hover on the label text. */
.field-help-label__label {
  position: relative;
  z-index: 1;
  pointer-events: auto;
}

.field-help-label__label--help {
  cursor: help;
  border-bottom: 1px dotted rgba(0, 0, 0, 0.35);
}

.field-help-label__flag {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  display: inline-flex;
  font-size: 0.85em;
  line-height: 1;
  margin-left: 1px;
  cursor: help;
  pointer-events: auto;
}

.field-help-label__text {
  display: block;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.82);
  white-space: normal;
}

.field-help-label__sheet {
  max-width: 360px;
}
</style>
